import { useMemo, useState } from 'react'

import type { Schemas } from '@npc/api-client'

import { Modal } from '../../components/modal'
import { useProjects, useUpdateMemo } from '../../app/queries'
import { t } from '../../copy'
import { T } from '../../copy/text'

/**
 * 移动确认层（设计稿 S16 / S17 / M07）。
 *
 * 确认层的 k/v 行回答每一个「会怎样」，**至少一行是否定式承诺**；
 * 备注解释 why。三条最重要的：
 *
 * - AI 权限要重新定——杂项里「不允许读」和项目里「不允许读」是两个
 *   不同的范围，服务端移动时会重置成 default，这里只告知，不代开。
 * - 项目删了这条跟着删。
 * - 移动是先加后删：成功前这条在原列表一直看得见。
 *
 * 目标失效（S17）：服务端拒绝时**留在原处**，给出「不会悄悄存到别的
 * 项目 / 不会丢掉这条 / 不会把归档项目自动恢复」三条否定式承诺。
 * 绝不静默回落成杂项。
 */
export function MoveMemoDialog(props: {
  memo: Schemas['Memo']
  onClose: () => void
  onMoved?: () => void
}) {
  const { memo } = props
  const projects = useProjects()
  const update = useUpdateMemo()
  const [target, setTarget] = useState<string>('')
  const [failure, setFailure] = useState<string | null>(null)

  // 项目内 ↔ 杂项两个方向都支持：目标列表排除当前作用域。
  const candidates = useMemo(
    () => (projects.data?.items ?? []).filter((p) => p.id !== memo.project_id),
    [projects.data?.items, memo.project_id],
  )

  async function move() {
    if (!target) return
    try {
      await update.mutateAsync({
        memoId: memo.id,
        projectId: target,
        expectedVersion: memo.version ?? 1,
      })
      props.onMoved?.()
      props.onClose()
    } catch (error) {
      // S17：留在原处，说清出了什么事。绝不回落成杂项。
      setFailure(error instanceof Error ? error.message : '')
    }
  }

  async function moveToMisc() {
    try {
      await update.mutateAsync({
        memoId: memo.id,
        projectId: null,
        expectedVersion: memo.version ?? 1,
      })
      props.onMoved?.()
      props.onClose()
    } catch (error) {
      setFailure(error instanceof Error ? error.message : '')
    }
  }

  // 纯语音条目 body 是空的——渲染空变量会变成一句缺字的话（copy 渲染层
  // 直接抛）。用真实时长那句话顶上，不编标题。
  const bodyPreview = memo.body
    ? (memo.body ?? '').length > 24
      ? `${(memo.body ?? '').slice(0, 24)}…`
      : memo.body
    : t('memo.detail.voice.title', {
        duration: `${Math.floor((memo.audio?.duration_ms ?? 0) / 1000)} 秒`,
      })

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) props.onClose()
      }}
      className="border border-m-line2 bg-m-bg1! text-m-t1 shadow-[var(--m-pop)]"
      title={t('memo.move.title')}
      description={t('memo.move.desc', { body: bodyPreview })}
    >
      <div data-testid="memo-move-dialog" className="flex flex-col gap-[12px]">
        <dl className="m-0 grid grid-cols-[110px_1fr] gap-x-[10px] gap-y-[7px]">
          <dt className="text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.row.who" />
          </dt>
          <dd className="m-0 text-[12.5px] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.row.who.v" />
          </dd>
          <dt className="text-[11.5px] font-medium text-m-wn-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.row.ai" />
          </dt>
          <dd className="m-0 text-[12.5px] text-m-wn-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.row.ai.v" />
          </dd>
          <dt className="text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.row.back" />
          </dt>
          <dd className="m-0 text-[12.5px] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.row.back.v" />
          </dd>
          <dt className="text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.row.delete" />
          </dt>
          <dd className="m-0 text-[12.5px] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.row.delete.v" />
          </dd>
        </dl>

        <select
          value={target}
          onChange={(event) => setTarget(event.target.value)}
          aria-label={t('memo.move.row.to')}
          data-testid="memo-move-target"
          className="h-[32px] w-full cursor-pointer rounded-[8px] border border-m-line2 bg-m-bg1 px-[10px] font-[inherit] text-[13px] text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        >
          <option value="">{t('memo.move.row.to')}…</option>
          {candidates.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <p className="m-0 text-[11.5px] leading-[1.6] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          <T id="memo.move.note" />
        </p>

        {failure ? (
          <div
            role="alert"
            data-testid="memo-move-failed"
            className="rounded-npc-card border border-m-wn-t/35 bg-m-wn-s p-[10px]"
          >
            <p className="m-0 text-[12px] font-medium text-m-wn-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id="memo.move.invalid.title" />
            </p>
            <p className="m-0 mt-[4px] text-[11.5px] text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              {t('memo.move.invalid.body', { reason: failure })}
            </p>
            <ul className="m-0 mt-[8px] list-none p-0 text-[11.5px] text-m-ok-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <li>
                <T id="memo.move.invalid.wont.1" />
              </li>
              <li>
                <T id="memo.move.invalid.wont.2" />
              </li>
              <li>
                <T id="memo.move.invalid.wont.3" />
              </li>
            </ul>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-[8px]">
          <button
            type="button"
            disabled={!target || update.isPending}
            onClick={() => void move()}
            data-testid="memo-move-confirm"
            className="h-[30px] cursor-pointer rounded-[8px] border-none bg-m-pri px-[16px] font-[inherit] text-[12.5px] font-medium text-m-pri-t disabled:cursor-not-allowed disabled:opacity-50"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          >
            {target
              ? t('memo.move.confirm', {
                  project: candidates.find((p) => p.id === target)?.name ?? '',
                })
              : t('memo.move.confirm.none')}
          </button>
          {memo.project_id ? (
            <button
              type="button"
              onClick={() => void moveToMisc()}
              disabled={update.isPending}
              data-testid="memo-move-to-misc"
              className="h-[30px] cursor-pointer rounded-[8px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[12px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            >
              <T id="memo.move.to.misc" />
            </button>
          ) : null}
          <span className="ml-auto text-[11px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.move.foot" />
          </span>
        </div>
      </div>
    </Modal>
  )
}
